import type { ComponentProps, MouseEvent } from 'react';

import { cn } from '@repo/lib/cn';
import { cva, type VariantProps } from 'class-variance-authority';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

export function InputGroup(inputGroupProps: ComponentProps<'div'>) {
  const { className, ...props } = inputGroupProps;
  return (
    <div
      data-slot="input-group"
      className={cn(
        'bg-input dark:bg-input/35 hover:bg-input-focus dark:hover:bg-input-focus/75 border-border-primary has-[[data-slot=input-group-control]:focus-within]:border-border-primary-focus has-[[data-slot=input-group-control]:focus-within]:ring-3 has-[[data-slot=input-group-control]:focus-within]:ring-focus-ring has-[[data-slot][aria-invalid=true]]:border-destructive/75 has-[[data-slot][aria-invalid=true]]:ring-3 has-[[data-slot][aria-invalid=true]]:ring-focus-ring-destructive/50 dark:has-[[data-slot][aria-invalid=true]]:ring-focus-ring-destructive has-disabled:bg-input/50 h-10 rounded-md border transition-colors in-data-[slot=combobox-content]:focus-within:border-inherit in-data-[slot=combobox-content]:focus-within:ring-0 has-disabled:opacity-60 has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>[data-align=block-end]]:[&>input]:pt-3 has-[>[data-align=block-start]]:[&>input]:pb-3 has-[>[data-align=inline-end]]:[&>input]:pr-1.5 has-[>[data-align=inline-start]]:[&>input]:pl-1.5 group/input-group relative flex w-full min-w-0 items-center outline-none ring-0 has-[textarea]:h-auto has-[textarea]:items-start',
        className
      )}
      {...props}
    />
  );
}

const inputGroupAddonVariants = cva(
  "text-muted-foreground h-auto gap-2 py-1.5 text-sm font-medium group-data-[disabled=true]/input-group:opacity-60 [&>kbd]:rounded-sm [&>svg:not([class*='size-'])]:size-4 flex cursor-text items-center justify-center select-none",
  {
    variants: {
      align: {
        'inline-start': 'pl-2 has-[>button]:ml-[-0.3rem] has-[>kbd]:ml-[-0.15rem] order-first',
        'inline-end': 'pr-2 has-[>button]:mr-[-0.3rem] has-[>kbd]:mr-[-0.15rem] order-last',
        'block-start': 'px-2.5 pt-2 group-has-[>input]/input-group:pt-2 [.border-b]:pb-2 order-first w-full justify-start',
        'block-end': 'px-2.5 pb-2 group-has-[>input]/input-group:pb-2 [.border-t]:pt-2 order-last w-full justify-start',
      },
    },
    defaultVariants: {
      align: 'inline-start',
    },
  }
);

export function InputGroupAddon(inputGroupAddonPropsReact: ComponentProps<'div'> & VariantProps<typeof inputGroupAddonVariants>) {
  const { className, align = 'inline-start', ...props } = inputGroupAddonPropsReact;

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    e.currentTarget.parentElement?.querySelector('input')?.focus();
  };

  return <div data-slot="input-group-addon" data-align={align} className={cn(inputGroupAddonVariants({ align }), className)} onClick={handleClick} {...props} />;
}

const inputGroupButtonVariants = cva('gap-2 text-sm shadow-none flex items-center', {
  variants: {
    size: {
      xs: "min-h-6 h-6 min-w-6 gap-1 rounded-sm px-1.5 [&>svg:not([class*='size-'])]:size-3.5",
      sm: "min-h-8 h-8 min-w-8 gap-1.5 rounded-sm px-2 [&>svg:not([class*='size-'])]:size-4",
    },
  },
  defaultVariants: {
    size: 'xs',
  },
});

export function InputGroupButton(inputGroupButtonPropsReact: Omit<ComponentProps<typeof Button>, 'size'> & VariantProps<typeof inputGroupButtonVariants>) {
  const { className, type = 'button', variant = 'ghost', size = 'sm', ...props } = inputGroupButtonPropsReact;

  return <Button type={type} data-size={size} variant={variant} className={cn(inputGroupButtonVariants({ size }), className)} {...props} />;
}

export function InputGroupText(inputGroupTextPropsReact: ComponentProps<'span'>) {
  const { className, ...props } = inputGroupTextPropsReact;
  return <span className={cn("text-muted-foreground gap-2 text-sm [&_svg:not([class*='size-'])]:size-4 flex items-center [&_svg]:pointer-events-none", className)} {...props} />;
}

export function InputGroupInput(inputGroupInputPropsReact: ComponentProps<'input'>) {
  const { className, ...props } = inputGroupInputPropsReact;
  return (
    <div data-slot="input-group-control" className="flex min-w-0 flex-1">
      <Input
        className={cn(
          'rounded-none border-0 bg-transparent hover:bg-transparent dark:hover:bg-transparent shadow-none outline-none ring-0 focus:ring-0 focus-visible:ring-0 disabled:bg-transparent aria-invalid:ring-0 dark:bg-transparent dark:disabled:bg-transparent w-full min-w-0',
          className
        )}
        {...props}
      />
    </div>
  );
}

export function InputGroupTextarea(inputGroupTextareaPropsReact: ComponentProps<'textarea'>) {
  const { className, ...props } = inputGroupTextareaPropsReact;

  return (
    <div data-slot="input-group-control" className="flex min-w-0 flex-1 self-stretch">
      <Textarea
        className={cn(
          'rounded-none border-0 bg-transparent hover:bg-transparent dark:hover:bg-transparent py-2 shadow-none outline-none ring-0 focus:ring-0 focus-visible:ring-0 disabled:bg-transparent aria-invalid:ring-0 dark:bg-transparent dark:disabled:bg-transparent w-full min-w-0 max-w-full resize-none flex-1',
          className
        )}
        {...props}
      />
    </div>
  );
}
